import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, FileSignature, MessageSquare, ArrowRight, History, Loader2 } from 'lucide-react';
import { getReviewHistory } from '../../services/reviewWorkflowApi';
import { Avatar, EmptyState } from '../ui';
import {
  SUBMIT_META,
  CURRENT_STATUS_META,
  VERSION_TOOLTIP,
  fmt,
  deciderOf,
  prepareCycles,
} from './reviewHistoryShared';

/**
 * At-a-glance review/attestation history for one reviewable entity (FECOA-1611).
 *
 * Deliberately simplified: one compact row per event (a submission, then its
 * decision) so each record fits on a single line and the whole story scans top
 * to bottom without scrolling sideways. The rich, per-event detail — decision
 * reasons and the full 21 CFR Part 11 e-signature block — lives on the
 * dedicated audit page reached via "View full audit history".
 */

// Small colored pill mirroring the event's meta. Kept inline so the row stays
// on one line at typical widths.
const EventPill = ({ meta }) => {
  const Icon = meta.icon;
  return (
    <span className={`badge ${meta.cls}`}>
      <Icon className="h-3.5 w-3.5" aria-hidden="true" />
      {meta.label}
    </span>
  );
};

// The "Details" cell: for a submission we surface the reviewed-version state;
// for a decision we surface muted markers that a reason and/or signature exist
// (the full text is on the audit page).
const versionCell = (version) => {
  if (version.kind === 'none') return null;
  if (version.kind === 'unchanged') {
    return (
      <span className="text-[12.5px] text-gray-400 dark:text-gray-500" title={VERSION_TOOLTIP}>
        Unchanged
      </span>
    );
  }
  return (
    <span className="inline-flex cursor-help items-center gap-1.5" title={VERSION_TOOLTIP}>
      <span className={`text-[12.5px] ${version.kind === 'reviewed' ? 'text-gray-500 dark:text-gray-400' : 'text-warning-700 dark:text-warning-400'}`}>
        {version.kind === 'reviewed' ? 'Reviewed' : 'Changed'}
      </span>
      <span className="code-chip">
        {version.hash}
      </span>
    </span>
  );
};

// Flatten prepared cycles into a single ordered list of one-line events,
// newest first so the latest activity is visible without scrolling (FECOA-1599).
const toEvents = (prepared) => {
  const events = [];
  prepared.forEach(({ r, decided, decisionMeta, version, standalone }) => {
    // Standalone actions (publish, revoke) are a single event by one actor —
    // no preceding "Submitted for review" row (FECOA-1694).
    if (standalone) {
      const actor = deciderOf(r);
      events.push({
        key: `${r.id}-action`,
        meta: decisionMeta,
        name: actor.name,
        email: actor.email,
        at: r.decided_at || r.submitted_at,
        hasReason: !!r.decision_reason,
        signed: r.signed_by_name || null,
      });
      return;
    }
    events.push({
      key: `${r.id}-submit`,
      meta: SUBMIT_META,
      name: r.submitted_by_name || 'Unknown',
      email: r.submitted_by_email,
      at: r.submitted_at,
      version,
    });
    if (decided) {
      const decider = deciderOf(r);
      events.push({
        key: `${r.id}-decision`,
        meta: decisionMeta,
        name: decider.name,
        email: decider.email,
        at: r.decided_at,
        hasReason: !!r.decision_reason,
        signed: r.signed_by_name || null,
      });
    } else {
      events.push({
        key: `${r.id}-awaiting`,
        meta: decisionMeta,
        awaiting: true,
      });
    }
  });
  return events.reverse();
};

// The one-line-per-event table, shared by the modal and the embeddable inline
// panel so both render identically (FECOA-1741).
const ReviewHistoryTable = ({ events }) => (
  <div className="overflow-x-auto">
    <table className="w-full">
      <thead>
        <tr className="border-b border-gray-100 dark:border-white/[0.06]">
          <th className="th-cell">Event</th>
          <th className="th-cell">By</th>
          <th className="th-cell">Details</th>
          <th className="th-cell whitespace-nowrap">When</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100 dark:divide-white/[0.06]">
        {events.map((e) => (
          <tr key={e.key} className="tr-interactive align-middle">
            <td className="td-cell py-3">
              <EventPill meta={e.meta} />
            </td>
            <td className="td-cell py-3">
              {e.awaiting ? (
                <span className="text-gray-400 dark:text-gray-500">—</span>
              ) : (
                <span className="inline-flex items-center gap-2">
                  <Avatar name={e.name} size="xs" />
                  <span>
                    <span className="font-medium text-gray-900 dark:text-gray-50">{e.name}</span>
                    {e.email && e.email !== e.name && (
                      <span className="text-gray-500 dark:text-gray-400"> ({e.email})</span>
                    )}
                  </span>
                </span>
              )}
            </td>
            <td className="td-cell py-3">
              {e.awaiting ? (
                <span className="text-[12.5px] text-gray-400 dark:text-gray-500">Awaiting decision</span>
              ) : e.version ? (
                versionCell(e.version)
              ) : (
                <span className="inline-flex items-center gap-3 text-gray-500 dark:text-gray-400">
                  {e.hasReason && (
                    <span className="inline-flex items-center gap-1.5 text-[12.5px]" title="A decision reason was recorded — see the full audit history.">
                      <MessageSquare className="h-4 w-4 text-gray-400 dark:text-gray-500" aria-hidden="true" /> Reason
                    </span>
                  )}
                  {e.signed && (
                    <span className="inline-flex items-center gap-1.5 text-[12.5px]" title="This decision was e-signed — see the full audit history.">
                      <FileSignature className="h-4 w-4 text-gray-400 dark:text-gray-500" aria-hidden="true" /> Signed
                    </span>
                  )}
                </span>
              )}
            </td>
            <td className="td-cell whitespace-nowrap py-3 text-[13px] tabular-nums text-gray-500 dark:text-gray-400">
              {e.awaiting ? '—' : fmt(e.at)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

// Shared data-loading hook body: fetch the raw review rows for an entity.
const useReviewHistoryRows = (entityType, objectId) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getReviewHistory(entityType, objectId);
        if (active) setRows(Array.isArray(data) ? data : data.results || []);
      } catch {
        if (active) setError('Could not load review history.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [entityType, objectId]);
  return { rows, loading, error };
};

// Chrome-less, embeddable review history: the same event table without the modal
// overlay/header/footer. Used in the Manage Assessment "Version History" tab so a
// reviewing admin sees the submission / denial / approval trail there, matching
// the card's audit history (FECOA-1741).
export const ReviewHistoryInline = ({ entityType = 'assessment', objectId }) => {
  const { rows, loading, error } = useReviewHistoryRows(entityType, objectId);
  const events = toEvents(prepareCycles(rows));

  if (loading) {
    return (
      <div className="flex items-center justify-center gap-2 px-5 py-8 text-[13px] text-gray-500 dark:text-gray-400">
        <Loader2 className="h-4 w-4 animate-spin text-primary-600 dark:text-primary-400" aria-hidden="true" />
        Loading review history…
      </div>
    );
  }
  if (error) return <div className="m-5 rounded-lg border border-danger-100 bg-danger-50 px-4 py-3 text-sm text-danger-700 dark:border-danger-500/30 dark:bg-danger-500/10 dark:text-danger-400">{error}</div>;
  if (events.length === 0) {
    return <EmptyState icon={History} title="No review activity yet for this assessment." />;
  }
  return <ReviewHistoryTable events={events} />;
};

const ReviewHistory = ({ entityType = 'assessment', objectId, entityName, currentStatus, onClose }) => {
  const navigate = useNavigate();
  const { rows, loading, error } = useReviewHistoryRows(entityType, objectId);

  const events = toEvents(prepareCycles(rows));
  const statusMeta = currentStatus ? CURRENT_STATUS_META[currentStatus] : null;

  const openFullPage = () => {
    onClose();
    navigate(`/review-history/${entityType}/${objectId}`, {
      state: { entityName, currentStatus },
    });
  };

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-gray-950/40 p-4 text-left whitespace-normal dark:bg-black/70"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="surface-overlay flex max-h-[80vh] w-full max-w-3xl flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 border-b border-gray-100 px-6 py-4 dark:border-white/[0.06]">
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-50">Review history</h3>
            {entityName && <p className="mt-1 truncate text-[13px] text-gray-500 dark:text-gray-400">{entityName}</p>}
          </div>
          <div className="flex flex-shrink-0 items-center gap-3">
            {statusMeta && (
              <span className={`badge badge-dot ${statusMeta.cls}`}>
                {statusMeta.label}
              </span>
            )}
            <button onClick={onClose} className="btn btn-ghost btn-sm btn-icon -mr-2" aria-label="Close">
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        </div>

        {/* Body — scrolls vertically; each event is one row */}
        <div className="flex-1 overflow-y-auto">
          {loading && (
            <div className="flex items-center justify-center gap-2 px-6 py-10 text-[13px] text-gray-500 dark:text-gray-400">
              <Loader2 className="h-4 w-4 animate-spin text-primary-600 dark:text-primary-400" aria-hidden="true" />
              Loading history…
            </div>
          )}
          {error && <div className="mx-6 my-5 rounded-lg border border-danger-100 bg-danger-50 px-4 py-3 text-sm text-danger-700 dark:border-danger-500/30 dark:bg-danger-500/10 dark:text-danger-400">{error}</div>}
          {!loading && !error && events.length === 0 && (
            <EmptyState icon={History} title="No review activity yet for this item." />
          )}

          {!loading && events.length > 0 && <ReviewHistoryTable events={events} />}
        </div>

        {/* Footer — link to the full audit page */}
        {!loading && !error && (
          <div className="flex flex-wrap items-center justify-end gap-2 border-t border-gray-100 bg-gray-50/70 px-6 py-3.5 dark:border-white/[0.06] dark:bg-white/[0.02]">
            <button
              onClick={openFullPage}
              className="btn btn-secondary btn-sm"
            >
              <span>View full audit history</span>
              <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReviewHistory;
